/**
 * A small JSON Schema (draft-07 subset) validator.
 *
 * The schemas live in `/content/schema` because they describe content, and
 * content is data (§19). The sim checks every file against its schema once,
 * at load, and refuses to start on a mismatch: authoring bugs should fail
 * at startup, not three hours into a run.
 *
 * Supported: type, enum, const, properties, required, additionalProperties,
 * items, minItems, maxItems, minimum, maximum, minLength, pattern, oneOf,
 * anyOf, allOf, and $ref to `#/definitions/...` in the same or a sibling
 * schema (`other.schema.json#/definitions/x`). That is everything the
 * content schemas use; add keywords here when a schema needs them.
 */

export interface JsonSchema {
  readonly $id?: string;
  readonly $ref?: string;
  readonly type?: string | readonly string[];
  readonly enum?: readonly unknown[];
  readonly const?: unknown;
  readonly properties?: Readonly<Record<string, JsonSchema>>;
  readonly required?: readonly string[];
  readonly additionalProperties?: boolean | JsonSchema;
  readonly items?: JsonSchema;
  readonly minItems?: number;
  readonly maxItems?: number;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly minLength?: number;
  readonly pattern?: string;
  readonly oneOf?: readonly JsonSchema[];
  readonly anyOf?: readonly JsonSchema[];
  readonly allOf?: readonly JsonSchema[];
  readonly definitions?: Readonly<Record<string, JsonSchema>>;
  readonly [key: string]: unknown;
}

export interface SchemaError {
  readonly path: string;
  readonly message: string;
}

/** Schemas by `$id`, so `$ref` can cross files. */
export type SchemaRegistry = Readonly<Record<string, JsonSchema>>;

function typeOf(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'number';
  return typeof value;
}

function matchesType(value: unknown, type: string): boolean {
  const actual = typeOf(value);
  if (type === 'number') return actual === 'number' || actual === 'integer';
  return actual === type;
}

function resolveRef(ref: string, current: JsonSchema, registry: SchemaRegistry): JsonSchema {
  const [file, pointer] = ref.split('#');
  const root = file === '' || file === undefined ? current : registry[file];
  if (!root) throw new Error(`Unknown schema in $ref: ${ref}`);
  let node: unknown = root;
  for (const part of (pointer ?? '').split('/').filter((p) => p !== '')) {
    if (typeof node !== 'object' || node === null || !(part in node)) {
      throw new Error(`Unresolvable $ref: ${ref}`);
    }
    node = (node as Record<string, unknown>)[part];
  }
  return node as JsonSchema;
}

function check(
  value: unknown,
  schema: JsonSchema,
  root: JsonSchema,
  registry: SchemaRegistry,
  path: string,
  errors: SchemaError[],
): void {
  if (schema.$ref !== undefined) {
    const [file] = schema.$ref.split('#');
    const target = resolveRef(schema.$ref, root, registry);
    const nextRoot = file === '' || file === undefined ? root : registry[file]!;
    check(value, target, nextRoot, registry, path, errors);
    return;
  }

  if (schema.type !== undefined) {
    const types = typeof schema.type === 'string' ? [schema.type] : schema.type;
    if (!types.some((t) => matchesType(value, t))) {
      errors.push({ path, message: `expected ${types.join(' | ')}, got ${typeOf(value)}` });
      return;
    }
  }

  if (schema.enum !== undefined && !schema.enum.some((e) => e === value)) {
    errors.push({ path, message: `expected one of ${JSON.stringify(schema.enum)}, got ${JSON.stringify(value)}` });
  }
  if (schema.const !== undefined && schema.const !== value) {
    errors.push({ path, message: `expected ${JSON.stringify(schema.const)}, got ${JSON.stringify(value)}` });
  }

  if (typeof value === 'number') {
    if (schema.minimum !== undefined && value < schema.minimum) errors.push({ path, message: `${value} < minimum ${schema.minimum}` });
    if (schema.maximum !== undefined && value > schema.maximum) errors.push({ path, message: `${value} > maximum ${schema.maximum}` });
  }

  if (typeof value === 'string') {
    if (schema.minLength !== undefined && value.length < schema.minLength) {
      errors.push({ path, message: `shorter than minLength ${schema.minLength}` });
    }
    if (schema.pattern !== undefined && !new RegExp(schema.pattern).test(value)) {
      errors.push({ path, message: `${JSON.stringify(value)} does not match ${schema.pattern}` });
    }
  }

  if (Array.isArray(value)) {
    if (schema.minItems !== undefined && value.length < schema.minItems) errors.push({ path, message: `fewer than minItems ${schema.minItems}` });
    if (schema.maxItems !== undefined && value.length > schema.maxItems) errors.push({ path, message: `more than maxItems ${schema.maxItems}` });
    if (schema.items !== undefined) {
      value.forEach((item, i) => check(item, schema.items!, root, registry, `${path}[${i}]`, errors));
    }
  }

  if (typeOf(value) === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of schema.required ?? []) {
      if (!(key in obj)) errors.push({ path, message: `missing required property "${key}"` });
    }
    const props = schema.properties ?? {};
    for (const [key, sub] of Object.entries(props)) {
      if (key in obj) check(obj[key], sub, root, registry, `${path}.${key}`, errors);
    }
    if (schema.additionalProperties === false) {
      for (const key of Object.keys(obj)) {
        if (!(key in props)) errors.push({ path: `${path}.${key}`, message: 'unexpected property' });
      }
    } else if (typeof schema.additionalProperties === 'object') {
      for (const key of Object.keys(obj)) {
        if (!(key in props)) check(obj[key], schema.additionalProperties, root, registry, `${path}.${key}`, errors);
      }
    }
  }

  if (schema.allOf !== undefined) {
    for (const sub of schema.allOf) check(value, sub, root, registry, path, errors);
  }
  if (schema.anyOf !== undefined || schema.oneOf !== undefined) {
    const alternatives = schema.oneOf ?? schema.anyOf!;
    const passing = alternatives.filter((sub) => validate(value, sub, registry, root, path).length === 0).length;
    if (passing === 0) errors.push({ path, message: `matched none of ${alternatives.length} alternatives` });
    if (schema.oneOf !== undefined && passing > 1) errors.push({ path, message: `matched ${passing} alternatives, expected exactly one` });
  }
}

/** Validate a value. An empty result means it conforms. */
export function validate(
  value: unknown,
  schema: JsonSchema,
  registry: SchemaRegistry = {},
  root: JsonSchema = schema,
  path = '$',
): SchemaError[] {
  const errors: SchemaError[] = [];
  check(value, schema, root, registry, path, errors);
  return errors;
}

/** Validate, throwing a readable error naming the file and every problem. */
export function assertValid(value: unknown, schema: JsonSchema, registry: SchemaRegistry, what: string): void {
  const errors = validate(value, schema, registry);
  if (errors.length === 0) return;
  const lines = errors.slice(0, 20).map((e) => `  ${e.path}: ${e.message}`);
  const more = errors.length > 20 ? `\n  ...and ${errors.length - 20} more` : '';
  throw new Error(`${what} does not match its schema:\n${lines.join('\n')}${more}`);
}
